import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatLakhsOrCr } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { BadgeIndianRupee, ShieldCheck } from "lucide-react";
import { DashboardInfoSheet } from "./DashboardInfoSheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import apiService from "@/services/apiService";

interface SanctionCustomerRow {
  customerName: string;
  sanctioned: number;
  utilized: number;
  available: number;
}

interface SanctionUtilization {
  sanctioned: number;
  utilized: number;
  available: number;
  customers: SanctionCustomerRow[];
}

const EMPTY: SanctionUtilization = {
  sanctioned: 0,
  utilized: 0,
  available: 0,
  customers: [],
};

const num = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

function normalizeSanctionUtilization(payload: unknown): SanctionUtilization {
  const root =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;
  if (!root || typeof root !== "object") return { ...EMPTY };

  const body = root as Record<string, unknown>;
  const customersRaw = Array.isArray(body.customers) ? body.customers : [];
  return {
    sanctioned: Math.round(num(body.sanctioned)),
    utilized: Math.round(num(body.utilized)),
    available: Math.round(num(body.available)),
    customers: customersRaw.map((row) => {
      const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
      return {
        customerName: String(r.customerName ?? r.customer_name ?? ""),
        sanctioned: Math.round(num(r.sanctioned)),
        utilized: Math.round(num(r.utilized)),
        available: Math.round(num(r.available)),
      };
    }),
  };
}

export const SanctionLimitCard = () => {
  const [data, setData] = useState<SanctionUtilization>(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/admin-dashboard-sanction-utilization"
        );
        if (cancelled) return;
        setData(normalizeSanctionUtilization(response));
      } catch (error) {
        console.error("Failed to load sanction utilization:", error);
        if (!cancelled) setData(EMPTY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { sanctioned, utilized, available, customers } = data;
  const safeTotal = Math.max(sanctioned, 1);
  const utilizationPercent = Math.min(100, Math.round((utilized / safeTotal) * 100));

  const getBarColor = () => {
    if (utilizationPercent >= 90) return "bg-destructive";
    if (utilizationPercent >= 70) return "bg-chart-gold";
    return "bg-primary";
  };

  const status =
    utilizationPercent >= 90 ? "Critical" : utilizationPercent >= 70 ? "Monitor" : "Healthy";

  return (
    <Card className="dashboard-panel h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="mt-1 flex items-center gap-2 text-base">
              <BadgeIndianRupee className="h-4 w-4 text-primary" />
              Sanction Utilization
            </CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {!loading && (
              <span
                className={cn(
                  "rounded-sm px-2 py-1 text-[10px] font-bold uppercase",
                  utilizationPercent >= 90
                    ? "bg-destructive/10 text-destructive"
                    : utilizationPercent >= 70
                      ? "bg-chart-gold/15 text-foreground"
                      : "bg-primary/10 text-primary"
                )}
              >
                {status}
              </span>
            )}
            <DashboardInfoSheet
              title="Sanction Utilization"
              description="From lender_status_history: sanctioned (status_id = 8) minus disbursed (status_id = 9)."
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer Name</TableHead>
                    <TableHead className="text-right">Sanctioned</TableHead>
                    <TableHead className="text-right">Utilized</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((row) => (
                    <TableRow key={row.customerName}>
                      <TableCell>{row.customerName}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatLakhsOrCr(row.sanctioned)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatLakhsOrCr(row.utilized)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatLakhsOrCr(row.available)}</TableCell>
                    </TableRow>
                  ))}
                  {customers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        No customers
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </DashboardInfoSheet>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 gap-4 border-y py-4">
          <div>
            <p className="dashboard-eyebrow">Sanctioned</p>
            {loading ? <Skeleton className="mt-2 h-8 w-24" /> : <p className="mt-1 text-2xl font-bold">{formatLakhsOrCr(sanctioned)}</p>}
          </div>
          <div>
            <p className="dashboard-eyebrow">Utilized</p>
            {loading ? <Skeleton className="mt-2 h-8 w-24" /> : <p className="mt-1 text-2xl font-bold">{formatLakhsOrCr(utilized)}</p>}
          </div>
          <div>
            <p className="dashboard-eyebrow">Available</p>
            {loading ? <Skeleton className="mt-2 h-8 w-24" /> : <p className="mt-1 text-2xl font-bold text-primary">{formatLakhsOrCr(available)}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              Portfolio utilization
            </span>
            <span className="text-sm font-bold">{loading ? "—" : `${utilizationPercent}%`}</span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-sm bg-secondary">
            <div
              className={cn("h-full rounded-full transition-all duration-500", getBarColor())}
              style={{ width: loading ? "0%" : `${utilizationPercent}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SanctionLimitCard;
